// Formatting for monthly invoices. Invoice months are US Eastern calendar months, so dates are shown
// in Eastern time too — a TruCheck initiated late on the 31st (Eastern) must read as that month.
const TIME_ZONE = "America/New_York";

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export const formatCents = (cents) =>
    cents === null || cents === undefined ? "—" : currency.format(cents / 100);

export const formatNumber = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-US"));

const valid = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDate = (value) => {
    const d = valid(value);
    return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TIME_ZONE }) : "—";
};

export const formatDateTime = (value) => {
    const d = valid(value);
    return d
        ? `${d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: TIME_ZONE })} ET`
        : "—";
};

// ISO-like "2026-10-14 09:30" in Eastern time, for CSV exports.
export const formatCsvDateTime = (value) => {
    const d = valid(value);
    if (!d) return "";
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
        timeZone: TIME_ZONE, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).formatToParts(d).map((x) => [x.type, x.value]));
    return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
};

// "2026-10" -> "October 2026".
export const monthLabel = (key) => {
    const m = /^(\d{4})-(\d{2})$/.exec(key || "");
    if (!m) return key || "—";
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 15)).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
};

// The last day of a month key, e.g. "2026-10" -> "Oct 31, 2026".
export const monthEndLabel = (key) => {
    const m = /^(\d{4})-(\d{2})$/.exec(key || "");
    if (!m) return "";
    const last = new Date(Date.UTC(Number(m[1]), Number(m[2]), 0, 12));
    return last.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};

export const extractApiError = (e, fallback) => {
    const data = e?.response?.data;
    if (typeof data?.error === "string" && data.error.trim()) return data.error;
    return fallback;
};

const csvCell = (value) => {
    const s = value === null || value === undefined ? "" : String(value);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Download rows (array of arrays, first = header) as a CSV file.
export const downloadCsv = (filename, rows) => {
    const csv = rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
    const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Save a base64 file returned by the API (e.g. the invoice PDF).
export const downloadBase64File = ({ fileName, contentType, contentBase64 }) => {
    const bytes = Uint8Array.from(atob(contentBase64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: contentType || "application/octet-stream" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};
